import { useCallback, useMemo, useState } from 'react';
import { Post } from '../model/post.types';
import { useDeletePost } from '../api/useDeletePost';
import { usePinPostAction } from '../lib/usePinPostAction';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { ActionConfirmModal } from './ActionConfirmModal';

type Props = {
  post: Post;
  pinnedCount: number;
};

export const OwnPostActionsMenu = ({ post, pinnedCount }: Props) => {
  const { communityId } = useCommunityContext();
  const deletePost = useDeletePost();
  const pin = usePinPostAction(post, communityId, pinnedCount);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const openDelete = useCallback(() => setConfirmingDelete(true), []);
  const dismiss = useCallback(() => setConfirmingDelete(false), []);
  const confirmDelete = useCallback(() => {
    analytics.trackPostDeleted(post);
    deletePost({ communityId, postId: post.id });
    setConfirmingDelete(false);
  }, [deletePost, communityId, post]);

  const items = useMemo<ActionMenuItem[]>(
    () => [
      {
        key: 'pin',
        label: 'Pin to top',
        disabled: pin.disabled,
        onClick: pin.toggle,
        toggle: { checked: pin.checked, helperText: pin.helperText },
      },
      {
        key: 'delete',
        label: 'Delete post',
        destructive: true,
        onClick: openDelete,
      },
    ],
    [pin.disabled, pin.toggle, pin.checked, pin.helperText, openDelete],
  );

  return (
    <>
      <ActionMenu items={items} />
      <ActionConfirmModal
        open={confirmingDelete}
        title="Are you sure you want to delete this post?"
        actionLabel="Delete"
        onConfirm={confirmDelete}
        onDismiss={dismiss}
      />
    </>
  );
};
